import { useEffect, useCallback, useRef, useState } from "react";

import { Crisp } from "crisp-sdk-web";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useCommandMenu } from "@/components/command-menu/useCommandMenu";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

export const CrispChatButton = observer(function CrispChatButton() {
  const t = useTranslations("sidebar");

  const { auth, view } = useStore();
  const prevUserRef = useRef(auth.currentUser);

  const CRISP_WEBSITE_ID = process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID;

  useEffect(() => {
    if (CRISP_WEBSITE_ID) {
      Crisp.configure(CRISP_WEBSITE_ID, { safeMode: true, autoload: false });
      Crisp.muteSound(true);
      Crisp.chat.close();
    }
  }, [CRISP_WEBSITE_ID]);

  useEffect(() => {
    if (CRISP_WEBSITE_ID) {
      const prevUser = prevUserRef.current;
      const { currentUser } = auth;

      if (currentUser) {
        if (auth.userData?.email) {
          Crisp.user.setEmail(auth.userData.email);
        }
        if (auth.userData?.name) {
          Crisp.user.setNickname(auth.userData.name);
        }
      } else if (prevUser) {
        Crisp.chat.close();
        Crisp.session.reset();
      }
      prevUserRef.current = currentUser;
    }
  }, [auth, auth.currentUser, CRISP_WEBSITE_ID]);

  const toggleChat = useCallback(() => {
    if (Crisp.chat.isChatOpened()) {
      Crisp.chat.close();
    } else {
      Crisp.chat.open();
    }
  }, []);

  const [chatOpened, setChatOpened] = useState(false);

  useEffect(() => {
    Crisp.chat.onChatOpened?.(() => setChatOpened(true));
    Crisp.chat.onChatClosed?.(() => setChatOpened(false));

    return () => {
      Crisp.chat.offChatOpened?.();
      Crisp.chat.offChatClosed?.();
    };
  }, []);

  useCommandMenu(
    () => ({
      id: "crisp-chat",
      icon: <Icon name="SupportChat" />,
      name: t("supportChat"),
      onSelect: () => {
        toggleChat();
      },
    }),
    [toggleChat, t],
  );

  return (
    <Sidebar.Button
      aria-pressed={chatOpened}
      className={twMerge(
        chatOpened &&
          "bg-sidebar-button-active-bg hover:bg-sidebar-button-active-bg [--color-icon-main:var(--color-icon-active)]  [--icon-secondary:var(--icon-active)]",
      )}
      icon={<Icon name="SupportChat" className="sm:w-[28px] sm:h-[28px]" />}
      title={t("supportChat")}
      onClick={toggleChat}
      hintKey="supportChat"
      disableTooltip={view.isHintsPanelVisible}
    />
  );
});
